import { useEffect, useState } from 'react';
import { previewDone, previewKey, previewShown, previewStart } from '../../../modules/q2c/quote-builder-state.mjs';

/**
 * Customer preview (LP-402): the quote exactly as the customer sees it. It is the server's own
 * customer document (GET /api/quotes/:id/preview: the same view model as the PDF and the public page,
 * built from customer fields only), shown in a sandboxed frame. Nothing internal is passed in here, so
 * no cost, margin, source, comparable or internal field can be rendered into it, not even hidden.
 * It shows the SAVED quote; unsaved edits show after Save. The document is kept with the revision it
 * is for (quote-builder-state.mjs previewShown): a new revision starts empty, and a failed request
 * shows the error, never a prior revision's document.
 */
export default function CustomerPreview({ quoteId, revision, dirty, onSave, saving }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    let dead = false;
    const key = previewKey(quoteId, revision);
    setDoc(previewStart(quoteId, revision));
    fetch(`/api/quotes/${quoteId}/preview`, { headers: { Authorization: `Bearer ${localStorage.getItem('mickey_token')}` } })
      .then(async (res) => {
        // An expired session goes to sign-in, as api.js does, instead of showing the refusal's words in the pane.
        if (res.status === 401) {
          localStorage.removeItem('mickey_token');
          window.location.href = '/login';
          throw new Error('Unauthorized');
        }
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Preview failed (${res.status})`);
        return res.text();
      })
      .then((t) => { if (!dead) setDoc((d) => previewDone(d, key, { html: t })); })
      .catch((e) => { if (!dead) setDoc((d) => previewDone(d, key, { error: e.message || 'Preview failed' })); });
    return () => { dead = true; };
  }, [quoteId, revision]);
  const { html, error: err } = previewShown(doc, quoteId, revision);

  return (
    <div className="space-y-3">
      <p className="rounded-lg bg-sky-50 px-3 py-2 text-sm text-sky-900 dark:bg-sky-900/30 dark:text-sky-100">This is the quote exactly as the customer would see it, built from customer fields only. Cost, margin and where prices came from are never on it.</p>
      {dirty && (
        <p className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
          Showing the saved quote. Save to see your changes here.
          {onSave && <button type="button" disabled={saving} onClick={onSave} className="rounded-md border border-amber-400 px-2 py-0.5 text-xs font-medium disabled:opacity-50">Save now</button>}
        </p>
      )}
      {err && <p className="text-sm text-red-600">{err}</p>}
      {html == null && !err && <p className="text-sm text-[var(--text-secondary)]">Loading the preview…</p>}
      {html != null && <iframe title="Customer preview" sandbox="" srcDoc={html} className="h-[80vh] w-full rounded-xl border border-[var(--border)] bg-white" />}
    </div>
  );
}
